import type { ApprovalDetail, OthersRow } from '@myrewards/contracts';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { meQuery } from '../../api';
import { waitingOnOthersQuery } from '../../api/actingFor';
import { othersTypeLine } from '../../acting-for/words';
import { keepIdsWhole } from '../../shell/Hero';
import { Icon } from '../../ui';
import o from './Others.module.css';
import s from '../Ops.module.css';

// APP-148 (LeadApprove board): what else waits on a Sector Lead, beside one of their entries and
// after deciding one for them: "Also waiting on Graham", "Still waiting on Graham".

/**
 * The lead's other work in Waiting on others (delegates' entries, awards returned to them), oldest
 * first, leaving out this award. Waiting on others, not the approvals list: it holds returned
 * awards too. Nothing is read in sample-roles Preview, where the server has nothing to say; in
 * Preview as a real person it's read, as that person acts fully (APP-122 §10, `me.preview` is
 * null there). `enabled` false reads nothing (an Executive's confirmation).
 */
export function useLeadWaiting(
  leadId: string,
  detail: ApprovalDetail,
  enabled = true,
): OthersRow[] {
  const { data: me } = useQuery(meQuery);
  const { data } = useQuery({
    ...waitingOnOthersQuery,
    enabled: enabled && me?.preview === null,
    retry: false,
  });
  return (data?.rows ?? []).filter(
    (row) => row.whose.person?.id === leadId && row.href !== `/approvals/${detail.award.id}`,
  );
}

/** The rows, each its ID linked and its type line, or "Nothing else is waiting on Graham." */
export function LeadWaitingList({
  rows,
  first,
}: {
  rows: readonly OthersRow[];
  first: string;
}): ReactElement {
  if (rows.length === 0)
    return (
      <p className={o.calm}>
        <Icon name="check" size={16} />
        <span>Nothing else is waiting on {first}.</span>
      </p>
    );
  return (
    <ul className={s.alsoList}>
      {rows.slice(0, 5).map((row) => (
        <li key={row.key}>
          <Link href={row.href} className={s.strongLink}>
            {keepIdsWhole(row.ref)}
          </Link>
          <span className={s.sub}>{othersTypeLine(row)}</span>
        </li>
      ))}
    </ul>
  );
}
